import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  TextField,
  Alert,
  Paper,
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  Inventory2,
  PersonOff,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  useProfitReport,
  useSalesReport,
  useLowStockReport,
  useInactiveUsersReport,
} from '../services/api';

const toDateInput = (date) => date.toISOString().split('T')[0];

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start_date: toDateInput(start),
    end_date: toDateInput(now),
  };
};

const getPreviousMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    start_date: toDateInput(start),
    end_date: toDateInput(end),
  };
};

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const key of keys) {
    if (obj[key] != null) return obj[key];
  }
  return null;
};

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.products)) return data.products;
  if (Array.isArray(data?.users)) return data.users;
  return [];
};

const formatMoney = (value) => {
  const amount = parseFloat(value ?? 0);
  return `${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'} د.أ`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const Dashboard = () => {
  const { user } = useAuth();
  const [profitRange, setProfitRange] = useState(getCurrentMonthRange);
  const [salesRange, setSalesRange] = useState(getPreviousMonthRange);
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [inactiveDays, setInactiveDays] = useState(30);

  const {
    data: profitReport,
    isLoading: profitLoading,
    error: profitError,
  } = useProfitReport(profitRange);

  const {
    data: salesReport,
    isLoading: salesLoading,
    error: salesError,
  } = useSalesReport(salesRange);

  const {
    data: lowStockReport,
    isLoading: lowStockLoading,
    error: lowStockError,
  } = useLowStockReport(lowStockThreshold);

  const {
    data: inactiveUsersReport,
    isLoading: inactiveUsersLoading,
    error: inactiveUsersError,
  } = useInactiveUsersReport(inactiveDays);

  const lowStockItems = useMemo(() => asArray(lowStockReport), [lowStockReport]);
  const inactiveUsers = useMemo(() => asArray(inactiveUsersReport), [inactiveUsersReport]);

  const totalProfit = pick(
    profitReport,
    'totalProfit',
    'profit',
    'total_profit',
    'netProfit',
    'net_profit'
  );

  const totalSales = pick(
    salesReport,
    'totalSales',
    'totalRevenue',
    'sales',
    'total_sales',
    'revenue',
    'totalAmount',
    'total_amount'
  );

  const orderCount = pick(
    salesReport,
    'orderCount',
    'order_count',
    'totalOrders',
    'total_orders',
    'ordersCount'
  );

  const statsData = [
    {
      title: 'إجمالي الربح',
      value: formatMoney(totalProfit),
      icon: <AttachMoney color="success" />,
      subtitle: `من ${profitRange.start_date} إلى ${profitRange.end_date}`,
    },
    {
      title: 'إجمالي المبيعات',
      value: formatMoney(totalSales),
      icon: <TrendingUp color="primary" />,
      subtitle: orderCount != null ? `${orderCount} طلب` : `من ${salesRange.start_date} إلى ${salesRange.end_date}`,
    },
    {
      title: 'منتجات منخفضة المخزون',
      value: String(lowStockItems.length),
      icon: <Inventory2 color="warning" />,
      subtitle: `الحد: ${lowStockThreshold}`,
    },
    {
      title: 'مستخدمون غير نشطين',
      value: String(inactiveUsers.length),
      icon: <PersonOff color="error" />,
      subtitle: `بدون طلبات خلال ${inactiveDays} يوم`,
    },
  ];

  const isLoading =
    profitLoading || salesLoading || lowStockLoading || inactiveUsersLoading;

  const hasError = profitError || salesError || lowStockError || inactiveUsersError;

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        مرحباً بعودتك، {user?.fullName || 'المدير'}!
      </Typography>

      {hasError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          تعذر تحميل بعض تقارير لوحة التحكم. تحقق من اتصال الـ API وحاول مرة أخرى.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="نطاق تقرير الأرباح" />
            <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="تاريخ البداية"
                type="date"
                size="small"
                value={profitRange.start_date}
                onChange={(e) =>
                  setProfitRange((prev) => ({ ...prev, start_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="تاريخ النهاية"
                type="date"
                size="small"
                value={profitRange.end_date}
                onChange={(e) =>
                  setProfitRange((prev) => ({ ...prev, end_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="نطاق تقرير المبيعات" />
            <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="تاريخ البداية"
                type="date"
                size="small"
                value={salesRange.start_date}
                onChange={(e) =>
                  setSalesRange((prev) => ({ ...prev, start_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="تاريخ النهاية"
                type="date"
                size="small"
                value={salesRange.end_date}
                onChange={(e) =>
                  setSalesRange((prev) => ({ ...prev, end_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {statsData.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {stat.subtitle}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'action.hover', width: 48, height: 48 }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title="منتجات منخفضة المخزون"
              subheader="منتجات عند أو أقل من حد المخزون"
              action={
                <TextField
                  label="الحد"
                  type="number"
                  size="small"
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(Number(e.target.value) || 0)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 0 }}
                />
              }
            />
            <Divider />
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>المنتج</TableCell>
                    <TableCell>الاسم بالعربية</TableCell>
                    <TableCell align="right">المخزون</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStockItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        لا توجد منتجات منخفضة المخزون.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lowStockItems.map((item) => (
                      <TableRow key={item.id ?? `${pick(item, 'name', 'productName')}-${pick(item, 'countity', 'quantity', 'stock')}`}>
                        <TableCell>{pick(item, 'name', 'productName', 'product_name') || '—'}</TableCell>
                        <TableCell>{pick(item, 'nameArabic', 'name_arabic', 'productNameArabic', 'product_name_arabic') || '—'}</TableCell>
                        <TableCell align="right">
                          {pick(item, 'countity', 'quantity', 'stock', 'availableQuantity', 'available_quantity') ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title="مستخدمون غير نشطين"
              subheader="مستخدمون بدون طلبات حديثة"
              action={
                <TextField
                  label="الأيام"
                  type="number"
                  size="small"
                  value={inactiveDays}
                  onChange={(e) => setInactiveDays(Number(e.target.value) || 0)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 1 }}
                />
              }
            />
            <Divider />
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>الاسم</TableCell>
                    <TableCell>البريد الإلكتروني</TableCell>
                    <TableCell>آخر طلب</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inactiveUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        لا يوجد مستخدمون غير نشطين.
                      </TableCell>
                    </TableRow>
                  ) : (
                    inactiveUsers.map((item) => (
                      <TableRow key={item.id ?? pick(item, 'email', 'fullName', 'full_name')}>
                        <TableCell>{pick(item, 'fullName', 'full_name', 'name', 'shopName', 'shop_name') || '—'}</TableCell>
                        <TableCell>{pick(item, 'email', 'userEmail', 'user_email') || '—'}</TableCell>
                        <TableCell>
                          {formatDate(
                            pick(
                              item,
                              'lastOrderDate',
                              'last_order_date',
                              'lastOrderAt',
                              'last_order_at',
                              'updatedAt',
                              'updated_at'
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
